import { Action } from 'redux';

import { CacheDateItem } from '../performance-data/cache-date.pd.model';
import {
    DataFilterOptions,
    FilterContext,
    LevelDataMap,
} from '../performance-data/data-filter-options.pd.model';
import {
    MetricData,
    MetricDataItem,
} from '../performance-data/metric.pd.model';
import { PeriodType } from '../widget/date-range.widget.model';

import {
    Category,
    Config,
    ConfigPath,
    ConfigPathLevel,
    ConfigView,
    ConfigViewMetric,
    ConfigViewPillar,
    DataKey,
    DefinitionSummary,
    HierarchyUserInfoItem,
    Metric,
    Override,
    RoleHierarchyItem,
    TrendPeriodStart,
} from './config.insight.model';
import { ComponentType } from './enums/component-type.enum';
import { Sort } from './experimental.grid';
import { MetricPath } from './insight.state';
import { MetricThreshold, PerformanceTrend } from './legend.insight.model';
import { OptionalColumn } from './optional-column.insight.model';
import { PresentationView } from './presentation.insight.model';
import { VisualItemHoverEvent } from './visual-item.insight.model';

export enum ActionType {
    INSIGHT__ROOT__LIST_DEFINITION_SUMMARY = 'INSIGHT__ROOT__LIST_DEFINITION_SUMMARY',
    INSIGHT__ROOT__LIST_DEFINITION_SUMMARY_RESULT = 'INSIGHT__ROOT__LIST_DEFINITION_SUMMARY_RESULT',
    INSIGHT__ROOT__LIST_DEFINITION_SUMMARY_CANCEL = 'INSIGHT__ROOT__LIST_DEFINITION_SUMMARY_CANCEL',
    INSIGHT__ROOT__SELECT_LEVEL_OPTION = 'INSIGHT__ROOT__SELECT_LEVEL_OPTION',
    INSIGHT__ROOT__SELECT_LEVEL_OPTION_RESULT = 'INSIGHT__ROOT__SELECT_LEVEL_OPTION_RESULT',
    INSIGHT__ROOT__RETRIEVE_CONFIG = 'INSIGHT__ROOT__RETRIEVE_CONFIG',
    INSIGHT__ROOT__RETRIEVE_CONFIG_RESULT = 'INSIGHT__ROOT__RETRIEVE_CONFIG_RESULT',
    INSIGHT__ROOT__RETRIEVE_CONFIG_CANCEL = 'INSIGHT__ROOT__RETRIEVE_CONFIG_CANCEL',
    INSIGHT__ROOT__RELOAD_CONFIG = 'INSIGHT__ROOT__RELOAD_CONFIG',
    INSIGHT__ROOT__RELOAD_CONFIG_RESULT = 'INSIGHT__ROOT__RELOAD_CONFIG_RESULT',
    INSIGHT__ROOT__RELOAD_CONFIG_CANCEL = 'INSIGHT__ROOT__RELOAD_CONFIG_CANCEL',
    INSIGHT__ROOT__SELECT_VISUAL_ITEM = 'INSIGHT__ROOT__SELECT_VISUAL_ITEM',
    INSIGHT__ROOT__SELECT_VISUAL_ITEM_RESULT = 'INSIGHT__ROOT__SELECT_VISUAL_ITEM_RESULT',
    INSIGHT__ROOT__SELECT_VISUAL_ITEM_CANCEL = 'INSIGHT__ROOT__SELECT_VISUAL_ITEM_CANCEL',

    INSIGHT__ROOT__SELECT_PRESENTATION_VIEW = 'INSIGHT__ROOT__SELECT_PRESENTATION_VIEW',
    INSIGHT__ROOT__SELECT_PATH = 'INSIGHT__ROOT__SELECT_PATH',
    INSIGHT__ROOT__SELECT_PILLARS = 'INSIGHT__ROOT__SELECT_PILLARS',
    INSIGHT__ROOT__SELECT_OPTIONAL_COLUMNS = 'INSIGHT__ROOT__SELECT_COLUMNS',
    INSIGHT__ROOT__SELECT_METRICS = 'INSIGHT__ROOT__SELECT_METRICS',
    INSIGHT__ROOT__SET_TREND_HIDE_FIRST_GRID_COLUMN = 'INSIGHT__ROOT__SET_TREND_HIDE_FIRST_GRID_COLUMN',
    INSIGHT__ROOT__CLEAR_SELECTED_VISUAL_ITEM = 'INSIGHT__ROOT__CLEAR_SELECTED_VISUAL_ITEM',

    INSIGHT__ROOT__CHECK_PREVIOUS_LEVELS_RECURSE = 'INSIGHT__ROOT__CHECK_PREVIOUS_LEVELS_RECURSE',
    INSIGHT__ROOT__CHECK_PREVIOUS_LEVELS_RECURSE_RESULT = 'INSIGHT__ROOT__CHECK_PREVIOUS_LEVELS_RECURSE_RESULT',

    INSIGHT__ROOT__OVERRIDE = 'INSIGHT__ROOT__OVERRIDE',

    INSIGHT__PERIOD__SELECT_PERIOD_TYPE = 'INSIGHT__PERIOD__SELECT_PERIOD_TYPE',
    INSIGHT__PERIOD__SELECT_PERIOD_TYPE_RESULT = 'INSIGHT__PERIOD__SELECT_PERIOD_TYPE_RESULT',
    INSIGHT__PERIOD__SELECT_PERIOD_TYPE_CANCEL = 'INSIGHT__PERIOD__SELECT_PERIOD_TYPE_CANCEL',
    INSIGHT__PERIOD__SELECT_EVENT_DATE = 'INSIGHT__PERIOD__SELECT_EVENT_DATE',

    INSIGHT__VISUAL__RETRIEVE_DATA = 'INSIGHT__VISUAL__RETRIEVE_DATA',
    INSIGHT__VISUAL__RETRIEVE_DATA_RESULT = 'INSIGHT__VISUAL__RETRIEVE_DATA_RESULT',
    INSIGHT__VISUAL__RETRIEVE_DATA_CANCEL = 'INSIGHT__VISUAL__RETRIEVE_DATA_CANCEL',
    INSIGHT__VISUAL__HOVER_ITEM = 'INSIGHT__VISUAL__HOVER_ITEM',

    INSIGHT__GRID__RETRIEVE_DATA = 'INSIGHT__GRID__RETRIEVE_DATA',
    INSIGHT__GRID__RETRIEVE_DATA_RESULT = 'INSIGHT__GRID__RETRIEVE_DATA_RESULT',
    INSIGHT__GRID__RETRIEVE_DATA_CANCEL = 'INSIGHT__GRID__RETRIEVE_DATA_CANCEL',
    INSIGHT__GRID__TOGGLE_COLUMN = 'INSIGHT__GRID__TOGGLE_COLUMN',
    INSIGHT__GRID__RESET_TOGGLE_COLUMN = 'INSIGHT__GRID__RESET_TOGGLE_COLUMN',
    INSIGHT__GRID__SORT = 'INSIGHT__GRID__SORT',
    INSIGHT__GRID__EXPORT = 'INSIGHT__GRID__EXPORT',
    INSIGHT__GRID__EXPORT_RESULT = 'INSIGHT__GRID__EXPORT_RESULT',

    INSIGHT__TREND__SELECT_PERIOD_TYPE = 'INSIGHT__TREND__SELECT_PERIOD_TYPE',
    INSIGHT__TREND__RETRIEVE_DATA = 'INSIGHT__TREND__RETRIEVE_DATA',
    INSIGHT__TREND__RETRIEVE_DATA_RESULT = 'INSIGHT__TREND__RETRIEVE_DATA_RESULT',
    INSIGHT__TREND__RETRIEVE_DATA_CANCEL = 'INSIGHT__TREND__RETRIEVE_DATA_CANCEL',

    INSIGHT__LEGEND__RETRIEVE_DATA = 'INSIGHT__LEGEND__RETRIEVE_DATA',
    INSIGHT__LEGEND__RETRIEVE_DATA_RESULT = 'INSIGHT__LEGEND__RETRIEVE_DATA_RESULT',
    INSIGHT__LEGEND__RETRIEVE_DATA_CANCEL = 'INSIGHT__LEGEND__RETRIEVE_DATA_CANCEL',
}

export interface RootListDefinitionSummaryAction extends Action<ActionType> {
    components?: string[];
}

export const rootListDefinitionSummary = (
    components?: string[],
): RootListDefinitionSummaryAction => ({
    type: ActionType.INSIGHT__ROOT__LIST_DEFINITION_SUMMARY,
    components,
});

export interface RootListDefinitionSummaryResultAction
    extends RootListDefinitionSummaryAction {
    summary?: Category[];
    category?: Category;
    subCategory?: Category;
    definition?: DefinitionSummary;
}

export const rootListDefinitionSummaryResult = (
    components?: string[],
    summary?: Category[],
    category?: Category,
    subCategory?: Category,
    definition?: DefinitionSummary,
): RootListDefinitionSummaryResultAction => ({
    type: ActionType.INSIGHT__ROOT__LIST_DEFINITION_SUMMARY_RESULT,
    components,
    summary,
    category,
    subCategory,
    definition,
});

export interface RootListDefinitionSummaryCancelAction
    extends Action<ActionType> {}

export const rootListDefinitionSummaryCancel =
    (): RootListDefinitionSummaryCancelAction => ({
        type: ActionType.INSIGHT__ROOT__LIST_DEFINITION_SUMMARY_CANCEL,
    });

/// //////////////////////

export interface RootSelectLevelOptionAction extends Action<ActionType> {
    category?: Category;
    subCategory?: Category;
    definition?: DefinitionSummary;
}

export const rootSelectLevelOption = (
    category?: Category,
    subCategory?: Category,
    definition?: DefinitionSummary,
): RootSelectLevelOptionAction => ({
    type: ActionType.INSIGHT__ROOT__SELECT_LEVEL_OPTION,
    category,
    subCategory,
    definition,
});

export interface RootSelectLevelOptionResultAction
    extends RootSelectLevelOptionAction {}

export const rootSelectLevelOptionResult = (
    category?: Category,
    subCategory?: Category,
    definition?: DefinitionSummary,
): RootSelectLevelOptionResultAction => ({
    type: ActionType.INSIGHT__ROOT__SELECT_LEVEL_OPTION_RESULT,
    category,
    subCategory,
    definition,
});

/// //////////////////////

export interface RootRetrieveConfigAction extends Action<ActionType> {
    identifier: string;
    component?: ComponentType;
    userId?: number;
    persistPeriod?: boolean;
}

export const rootRetrieveConfig = (
    identifier: string,
    component?: ComponentType,
    userId?: number,
    persistPeriod?: boolean,
): RootRetrieveConfigAction => ({
    type: ActionType.INSIGHT__ROOT__RETRIEVE_CONFIG,
    identifier,
    component,
    userId,
    persistPeriod,
});

export interface RootRetrieveConfigResultAction
    extends RootRetrieveConfigAction {
    periodType: PeriodType;
    cacheDates: CacheDateItem[];
    eventDate: Date;
    gotoDashboard: boolean;
    initialDataFilterOpts: DataFilterOptions;
    trendPeriods: TrendPeriodStart[];
    config: Config;
    configUserInfo: HierarchyUserInfoItem;
    view: ConfigView;
    viewContext: FilterContext;
    path: ConfigPath;
    roleHierarchy: RoleHierarchyItem[];
    cacheLangId: number;
    optionalColumns: OptionalColumn[];
}

export const rootRetrieveConfigResult = (
    identifier?: string,
    component?: ComponentType,
    userId?: number,
    periodType?: PeriodType,
    cacheDates?: CacheDateItem[],
    eventDate?: Date,
    gotoDashboard?: boolean,
    initialDataFilterOpts?: DataFilterOptions,
    trendPeriods?: TrendPeriodStart[],
    config?: Config,
    configUserInfo?: HierarchyUserInfoItem,
    view?: ConfigView,
    viewContext?: FilterContext,
    path?: ConfigPath,
    roleHierarchy?: RoleHierarchyItem[],
    cacheLangId?: number,
    optionalColumns?: OptionalColumn[],
): RootRetrieveConfigResultAction => ({
    type: ActionType.INSIGHT__ROOT__RETRIEVE_CONFIG_RESULT,
    identifier,
    component,
    userId,
    periodType,
    cacheDates,
    eventDate,
    gotoDashboard,
    initialDataFilterOpts,
    trendPeriods,
    config,
    configUserInfo,
    view,
    viewContext,
    path,
    roleHierarchy,
    cacheLangId,
    optionalColumns,
});

/// //////////////////////

export interface RootReloadConfigAction extends RootRetrieveConfigAction {}

export const rootReloadConfig = (
    identifier: string,
    component?: ComponentType,
    userId?: number,
): RootReloadConfigAction => ({
    type: ActionType.INSIGHT__ROOT__RELOAD_CONFIG,
    identifier,
    component,
    userId,
});

export interface RootReloadConfigResultAction extends RootReloadConfigAction {
    config: Config;
    configUserInfo: HierarchyUserInfoItem;
    view: ConfigView;
    viewContext: FilterContext;
    path: ConfigPath;
    initialDataFilterOpts: DataFilterOptions;
    cacheLangId: number;
    optionalColumns: OptionalColumn[];
}

export const rootReloadConfigResult = (
    identifier?: string,
    component?: ComponentType,
    userId?: number,
    config?: Config,
    configUserInfo?: HierarchyUserInfoItem,
    view?: ConfigView,
    viewContext?: FilterContext,
    path?: ConfigPath,
    initialDataFilterOpts?: DataFilterOptions,
    cacheLangId?: number,
    optionalColumns?: OptionalColumn[],
): RootReloadConfigResultAction => ({
    type: ActionType.INSIGHT__ROOT__RELOAD_CONFIG_RESULT,
    identifier,
    component,
    userId,
    config,
    configUserInfo,
    view,
    viewContext,
    path,
    initialDataFilterOpts,
    cacheLangId,
    optionalColumns,
});

/// //////////////////////

export interface RootSelectVisualItemAction extends Action<ActionType> {
    viewPillar: ConfigViewPillar;
    viewMetric: ConfigViewMetric;
    metric: Metric;
    dataMetric: MetricDataItem;
}

export const rootSelectVisualItem = (
    viewPillar: ConfigViewPillar,
    viewMetric: ConfigViewMetric,
    metric: Metric,
    dataMetric: MetricDataItem,
): RootSelectVisualItemAction => ({
    type: ActionType.INSIGHT__ROOT__SELECT_VISUAL_ITEM,
    viewPillar,
    viewMetric,
    metric,
    dataMetric,
});

export interface RootSelectVisualItemResultAction
    extends RootSelectVisualItemAction {
    // path: ConfigPath;
    pillars: ConfigViewPillar[];
    supportedColumns: OptionalColumn[];
    columns: OptionalColumn[];
    metrics: MetricPath[];
}

export const rootSelectVisualItemResult = (
    viewPillar?: ConfigViewPillar,
    viewMetric?: ConfigViewMetric,
    metric?: Metric,
    dataMetric?: MetricDataItem,
    // path?: ConfigPath,
    pillars?: ConfigViewPillar[],
    supportedColumns?: OptionalColumn[],
    columns?: OptionalColumn[],
    metrics?: MetricPath[],
): RootSelectVisualItemResultAction => ({
    type: ActionType.INSIGHT__ROOT__SELECT_VISUAL_ITEM_RESULT,
    viewPillar,
    viewMetric,
    metric,
    dataMetric,
    // path,
    pillars,
    supportedColumns,
    columns,
    metrics,
});

/// //////////////////////

export interface RootSelectPresentationViewAction extends Action<ActionType> {
    presentationView: PresentationView;
}

export const rootSelectPresentationView = (
    presentationView: PresentationView,
): RootSelectPresentationViewAction => ({
    type: ActionType.INSIGHT__ROOT__SELECT_PRESENTATION_VIEW,
    presentationView,
});

/// //////////////////////

export interface RootSelectPathAction extends Action<ActionType> {
    path: ConfigPath;
}

export const rootSelectPath = (path: ConfigPath): RootSelectPathAction => ({
    type: ActionType.INSIGHT__ROOT__SELECT_PATH,
    path,
});

/// //////////////////////

export interface RootSelectPillarsAction extends Action<ActionType> {
    pillars: ConfigViewPillar[];
}

export const rootSelectPillars = (
    pillars: ConfigViewPillar[],
): RootSelectPillarsAction => ({
    type: ActionType.INSIGHT__ROOT__SELECT_PILLARS,
    pillars,
});

/// //////////////////////

export interface RootSelectOptionalColumnsAction extends Action<ActionType> {
    columns: OptionalColumn[];
}

export const rootSelectOptionalColumns = (
    columns: OptionalColumn[],
): RootSelectOptionalColumnsAction => ({
    type: ActionType.INSIGHT__ROOT__SELECT_OPTIONAL_COLUMNS,
    columns,
});

/// //////////////////////

export interface RootSelectMetricsAction extends Action<ActionType> {
    metrics: MetricPath[];
}

export const rootSelectMetrics = (
    metrics: MetricPath[],
): RootSelectMetricsAction => ({
    type: ActionType.INSIGHT__ROOT__SELECT_METRICS,
    metrics,
});

/// //////////////////////

export interface RootSetTrendHideFirstGridColumnAction
    extends Action<ActionType> {
    trendHideFirstGridColumn: boolean;
}

export const rootSetTrendHideFirstGridColumn = (
    trendHideFirstGridColumn: boolean,
): RootSetTrendHideFirstGridColumnAction => ({
    type: ActionType.INSIGHT__ROOT__SET_TREND_HIDE_FIRST_GRID_COLUMN,
    trendHideFirstGridColumn,
});

/// //////////////////////

export interface RootClearSelectedVisualItemAction extends Action<ActionType> {}

export const rootClearSelectedVisualItem =
    (): RootClearSelectedVisualItemAction => ({
        type: ActionType.INSIGHT__ROOT__CLEAR_SELECTED_VISUAL_ITEM,
    });

/// //////////////////////

export interface RootCheckPreviousLevelsRecurseAction
    extends Action<ActionType> {
    identifier: string;
}

export const rootCheckPreviousLevelsRecurse = (
    identifier: string,
): RootCheckPreviousLevelsRecurseAction => ({
    type: ActionType.INSIGHT__ROOT__CHECK_PREVIOUS_LEVELS_RECURSE,
    identifier,
});

export interface RootCheckPreviousLevelsRecurseResultAction
    extends RootCheckPreviousLevelsRecurseAction {
    levels: ConfigPathLevel[];
}

export const rootCheckPreviousLevelsRecurseResult = (
    identifier?: string,
    levels?: ConfigPathLevel[],
): RootCheckPreviousLevelsRecurseResultAction => ({
    type: ActionType.INSIGHT__ROOT__CHECK_PREVIOUS_LEVELS_RECURSE_RESULT,
    identifier,
    levels,
});

/// //////////////////////

export interface RootOverrideAction extends Action<ActionType> {
    override?: Override;
    view?: ConfigView;
    defaultPath?: string;
    path?: ConfigPath;
    initialDataFilterOpts?: DataFilterOptions;
}

export const rootOverride = (
    override?: Override,
    view?: ConfigView,
    defaultPath?: string,
    path?: ConfigPath,
    initialDataFilterOpts?: DataFilterOptions,
): RootOverrideAction => ({
    type: ActionType.INSIGHT__ROOT__OVERRIDE,
    override,
    view,
    defaultPath,
    path,
    initialDataFilterOpts,
});

/// //////////////////////

export interface PeriodSelectPeriodTypeAction extends Action<ActionType> {
    periodType: PeriodType;
}

export const periodSelectPeriodType = (
    periodType: PeriodType,
): PeriodSelectPeriodTypeAction => ({
    type: ActionType.INSIGHT__PERIOD__SELECT_PERIOD_TYPE,
    periodType,
});

export interface PeriodSelectPeriodTypeResultAction
    extends PeriodSelectPeriodTypeAction {
    cacheDates: CacheDateItem[];
    eventDate: Date;
    gotoDashboard: boolean;
    trendPeriods: TrendPeriodStart[];
}

export const periodSelectPeriodTypeResult = (
    periodType?: PeriodType,
    cacheDates?: CacheDateItem[],
    eventDate?: Date,
    gotoDashboard?: boolean,
    trendPeriods?: TrendPeriodStart[],
): PeriodSelectPeriodTypeResultAction => ({
    type: ActionType.INSIGHT__PERIOD__SELECT_PERIOD_TYPE_RESULT,
    periodType,
    cacheDates,
    eventDate,
    gotoDashboard,
    trendPeriods,
});

/// //////////////////////

export interface PeriodSelectEventDateAction extends Action<ActionType> {
    eventDate: Date;
}

export const periodSelectEventDate = (
    eventDate: Date,
): PeriodSelectEventDateAction => ({
    type: ActionType.INSIGHT__PERIOD__SELECT_EVENT_DATE,
    eventDate,
});

/// //////////////////////

export interface VisualRetrieveDataAction extends Action<ActionType> {
    opts: DataFilterOptions;
    parentLevelsDataMap: LevelDataMap[];
}

export const visualRetrieveData = (
    opts: DataFilterOptions,
    parentLevelsDataMap?: LevelDataMap[],
): VisualRetrieveDataAction => ({
    type: ActionType.INSIGHT__VISUAL__RETRIEVE_DATA,
    opts,
    parentLevelsDataMap,
});

export interface VisualRetrieveDataResultAction
    extends VisualRetrieveDataAction {
    data: MetricData;
    peerGroupData?: MetricData;
}

export const visualRetrieveDataResult = (
    opts?: DataFilterOptions,
    parentLevelsDataMap?: LevelDataMap[],
    data?: MetricData,
    peerGroupData?: MetricData,
): VisualRetrieveDataResultAction => ({
    type: ActionType.INSIGHT__VISUAL__RETRIEVE_DATA_RESULT,
    opts,
    parentLevelsDataMap,
    data,
    peerGroupData,
});

export interface VisualRetrieveDataCancelAction extends Action<ActionType> {}

export const visualRetrieveDataCancel = (): VisualRetrieveDataCancelAction => ({
    type: ActionType.INSIGHT__VISUAL__RETRIEVE_DATA_CANCEL,
});

/// //////////////////////

export interface VisualHoverItemAction extends Action<ActionType> {
    item: VisualItemHoverEvent;
}

export const visualHoverItem = (
    item: VisualItemHoverEvent,
): VisualHoverItemAction => ({
    type: ActionType.INSIGHT__VISUAL__HOVER_ITEM,
    item,
});

/// //////////////////////

export interface GridRetrieveDataAction extends Action<ActionType> {
    opts: DataFilterOptions;
    rowId: string;
    parentLevelsDataMap: LevelDataMap[];
}

export const gridRetrieveData = (
    opts: DataFilterOptions,
    rowId: string,
    parentLevelsDataMap?: LevelDataMap[],
): GridRetrieveDataAction => ({
    type: ActionType.INSIGHT__GRID__RETRIEVE_DATA,
    opts,
    rowId,
    parentLevelsDataMap,
});

export interface GridRetrieveDataResultAction extends GridRetrieveDataAction {
    data: DataKey[];
    peerGroupData: DataKey[];
    teamSuperGroupData: DataKey[];
    superGroupData: DataKey[];
    hierarchyUserInfo: HierarchyUserInfoItem[];
    hierarchyUserInfoCurrent: HierarchyUserInfoItem[];
}

export const gridRetrieveDataResult = (
    opts?: DataFilterOptions,
    rowId?: string,
    parentLevelsDataMap?: LevelDataMap[],
    data?: DataKey[],
    peerGroupData?: DataKey[],
    teamSuperGroupData?: DataKey[],
    superGroupData?: DataKey[],
    hierarchyUserInfo?: HierarchyUserInfoItem[],
    hierarchyUserInfoCurrent?: HierarchyUserInfoItem[],
): GridRetrieveDataResultAction => ({
    type: ActionType.INSIGHT__GRID__RETRIEVE_DATA_RESULT,
    opts,
    rowId,
    parentLevelsDataMap,
    data,
    peerGroupData,
    teamSuperGroupData,
    superGroupData,
    hierarchyUserInfo,
    hierarchyUserInfoCurrent,
});

export interface GridRetrieveDataCancelAction extends Action<ActionType> {
    rowIds?: string[];
}

export const gridRetrieveDataCancel = (
    rowIds?: string[],
): GridRetrieveDataCancelAction => ({
    type: ActionType.INSIGHT__GRID__RETRIEVE_DATA_CANCEL,
    rowIds,
});

/// //////////////////////

export interface GridToggleColumnAction extends Action<ActionType> {
    columnId: string;
    pillarExpanded: boolean;
    metricExpanded: boolean;
}

export const gridToggleColumn = (
    columnId: string,
    pillarExpanded: boolean,
    metricExpanded: boolean,
): GridToggleColumnAction => ({
    type: ActionType.INSIGHT__GRID__TOGGLE_COLUMN,
    columnId,
    pillarExpanded,
    metricExpanded,
});

/// //////////////////////

export interface GridResetToggleColumnAction extends Action<ActionType> {}

export const gridResetToggleColumn = (): GridResetToggleColumnAction => ({
    type: ActionType.INSIGHT__GRID__RESET_TOGGLE_COLUMN,
});

/// //////////////////////

export interface GridSortAction extends Action<ActionType> {
    rowIds: string[];
    def: Sort;
}

export const gridSort = (rowIds: string[], def: Sort): GridSortAction => ({
    type: ActionType.INSIGHT__GRID__SORT,
    rowIds,
    def,
});

/// //////////////////////

export interface GridExportAction extends Action<ActionType> {
    filename: string;
}

export const gridExport = (filename: string): GridExportAction => ({
    type: ActionType.INSIGHT__GRID__EXPORT,
    filename,
});

export interface GridExportResultAction extends GridExportAction {}

export const gridExportResult = (
    filename?: string,
): GridExportResultAction => ({
    type: ActionType.INSIGHT__GRID__EXPORT_RESULT,
    filename,
});

/// //////////////////////

export interface TrendSelectPeriodTypeAction extends Action<ActionType> {
    periodType: PeriodType;
}

export const trendSelectPeriodType = (
    periodType: PeriodType,
): TrendSelectPeriodTypeAction => ({
    type: ActionType.INSIGHT__TREND__SELECT_PERIOD_TYPE,
    periodType,
});

/// //////////////////////

export interface TrendRetrieveDataAction extends Action<ActionType> {
    opts: DataFilterOptions;
    rowId: string;
    parentLevelsDataMap: LevelDataMap[];
}

export const trendRetrieveData = (
    opts: DataFilterOptions,
    rowId: string,
    parentLevelsDataMap?: LevelDataMap[],
): TrendRetrieveDataAction => ({
    type: ActionType.INSIGHT__TREND__RETRIEVE_DATA,
    opts,
    rowId,
    parentLevelsDataMap,
});

export interface TrendRetrieveDataResultAction extends TrendRetrieveDataAction {
    data: DataKey[];
    peerGroupData: DataKey[];
}

export const trendRetrieveDataResult = (
    opts?: DataFilterOptions,
    rowId?: string,
    parentLevelsDataMap?: LevelDataMap[],
    data?: DataKey[],
    peerGroupData?: DataKey[],
): TrendRetrieveDataResultAction => ({
    type: ActionType.INSIGHT__TREND__RETRIEVE_DATA_RESULT,
    opts,
    rowId,
    parentLevelsDataMap,
    data,
    peerGroupData,
});

export interface TrendRetrieveDataCancelAction extends Action<ActionType> {
    rowIds?: string[];
}

export const trendRetrieveDataCancel = (
    rowIds?: string[],
): TrendRetrieveDataCancelAction => ({
    type: ActionType.INSIGHT__TREND__RETRIEVE_DATA_CANCEL,
    rowIds,
});

/// //////////////////////

export interface LegendRetrieveDataAction extends Action<ActionType> {}

export const legendRetrieveData = (): LegendRetrieveDataAction => ({
    type: ActionType.INSIGHT__LEGEND__RETRIEVE_DATA,
});

export interface LegendRetrieveDataResultAction
    extends LegendRetrieveDataAction {
    metricThresholds: MetricThreshold[];
    trendsArrows: PerformanceTrend[];
}

export const legendRetrieveDataResult = (
    metricThresholds?: MetricThreshold[],
    trendsArrows?: PerformanceTrend[],
): LegendRetrieveDataResultAction => ({
    type: ActionType.INSIGHT__LEGEND__RETRIEVE_DATA_RESULT,
    metricThresholds,
    trendsArrows,
});
